import React, { memo, useCallback, useEffect, useMemo, useRef, useState, Fragment } from 'react';
import { connect } from 'react-redux';
import Cookies from 'js-cookie';
import { Tour } from '@web/web5';
import { USAGE_LIMITS_DELETE_ITEMS, USAGE_LIMITS_MANAGE_DUPLICATES } from 'constants/ActionTypes';
import { getLiteral, getLiteralWithParameters } from 'utils/getLiteral';
import { getCookieDomainName } from 'utils/routes';
import { COOKIE_USAGE_LIMITS } from 'containers/settings/LimitsManagement/constants';
import ToursOverlay from 'containers/components/ToursOverlay';

import './styles.scss';

const TOURS = {
    DELETE: USAGE_LIMITS_DELETE_ITEMS,
    MANAGE_DUPLICATES: USAGE_LIMITS_MANAGE_DUPLICATES,
};

// Here we define the CSS selectors that will be used to generate the tour overlay's mask
// and as selectors for each tour step
const TARGETS = {
    DELETE: [
        ['.ag-header .ag-pinned-left-header', '.ag-pinned-left-cols-container .ag-row:first-child'],
        ['.ag-pinned-left-cols-container .ag-row:first-child', '.bulk-operation-delete'],
    ],
    MANAGE_DUPLICATES: [
        ['.fm-entity-menu-left [class*="viewsChip"]', '[class*="QuickFilters__popoverCustomWide"]'],
    ],
};

const PREREQUISITES = {
    DELETE: ['.ag-pinned-left-cols-container .ag-row:first-child .w5-cell-checkbox__svg-container'],
    MANAGE_DUPLICATES: [TARGETS.MANAGE_DUPLICATES[0][0]],
};

const mapStateToProps = (state) => {
    const entityList = state.entityList;
    const activeEntity = entityList.active;
    return { loading: entityList?.[activeEntity]?.loading };
};

const UsageLimitTours = ({ entity, loading }) => {
    const [activeTour, setActiveTour] = useState(null);
    const [targets, setTargets] = useState(null);
    const [tourToShow, setTourToShow] = useState(null);
    const timeout = useRef(null);

    useEffect(() => {
        const tour = Cookies.get(COOKIE_USAGE_LIMITS, {
            domain: getCookieDomainName(window.location.hostname),
        });
        setTourToShow(tour);
    }, []);

    const isTourAvailable = useCallback(() => {
        const checkSelectors = (selectors) => {
            return selectors.every((selector) => {
                return document.querySelectorAll(selector).length !== 0;
            });
        };

        switch (tourToShow) {
            case TOURS.DELETE:
                return checkSelectors(PREREQUISITES.DELETE);
            case TOURS.MANAGE_DUPLICATES:
                return checkSelectors(PREREQUISITES.MANAGE_DUPLICATES);
            default:
                return false;
        }
    }, [tourToShow]);

    const toggleDelete = useCallback(() => {
        const checkEl = document.querySelector(PREREQUISITES.DELETE[0]);
        checkEl?.click();
    }, []);

    const tourSchemas = useMemo(() => {
        const entityName = getLiteral(`label_${entity.trueName}`);

        return {
            [TOURS.DELETE]: {
                steps: [
                    {
                        selector: TARGETS.DELETE[0][1],
                        title: getLiteralWithParameters('label_tour_multi_selection_title', [
                            entityName,
                        ]),
                        description: getLiteralWithParameters('label_tour_multi_selection_desc', [
                            entityName,
                        ]),
                        onInit: () => {
                            toggleDelete();
                            setTargets(TARGETS.DELETE[0]);
                        },
                    },
                    {
                        selector: TARGETS.DELETE[1][1],
                        title: getLiteralWithParameters('label_tour_bulk_delete_title', [
                            entityName,
                        ]),
                        description: getLiteralWithParameters('label_tour_buld_delete_desc', [
                            entityName,
                        ]),
                        onReady: () => setTargets(TARGETS.DELETE[1]),
                    },
                ],
                onClose: toggleDelete,
            },
            [TOURS.MANAGE_DUPLICATES]: {
                onBeforeTour: () => {
                    // Open views dropdown in quickfilters
                    const viewsEl = document.querySelector(TARGETS.MANAGE_DUPLICATES[0][0]);
                    viewsEl?.click();
                },
                steps: [
                    {
                        selector: TARGETS.MANAGE_DUPLICATES[0][1],
                        title: getLiteralWithParameters('label_tour_duplicate_title', [entityName]),
                        description: getLiteralWithParameters('label_tour_duplicate_desc', [
                            entityName,
                        ]),
                        onInit: () => {
                            setTargets(TARGETS.MANAGE_DUPLICATES[0]);
                            document
                                .querySelector(TARGETS.MANAGE_DUPLICATES[0][1])
                                .setAttribute('id', 'fm-force-visible');
                        },
                    },
                ],
                onClose: () => {
                    document.querySelector(TARGETS.MANAGE_DUPLICATES[0][1]).removeAttribute('id');
                },
            },
        };
    }, [entity.trueName, toggleDelete]);

    const handleOnClose = useCallback(() => {
        setTargets(null);
        setActiveTour(null);
        setTourToShow(null);
        tourToShow &&
            Cookies.remove(COOKIE_USAGE_LIMITS, {
                domain: getCookieDomainName(window.location.hostname),
            });
        activeTour?.onClose && activeTour.onClose();
    }, [activeTour, tourToShow]);

    useEffect(() => {
        if (loading) return;

        // We have to wait for the page loading related animations to finish before initializing the tour
        timeout.current = setTimeout(() => {
            const tour = tourSchemas[tourToShow];
            if (tour && isTourAvailable()) {
                tour.onBeforeTour && tour.onBeforeTour();
                setActiveTour(tour);
            } else {
                handleOnClose();
            }
        }, 1000);

        return () => {
            if (timeout.current) clearTimeout(timeout.current);
        };
    }, [handleOnClose, isTourAvailable, loading, tourSchemas, tourToShow]);

    const shouldShowTour = useMemo(
        () => !loading && activeTour?.steps?.length > 0,
        [activeTour, loading],
    );

    return shouldShowTour ? (
        <Fragment>
            <ToursOverlay targets={targets} />
            <Tour steps={activeTour.steps} onClose={handleOnClose} delay={600} />
        </Fragment>
    ) : null;
};

export default memo(connect(mapStateToProps)(UsageLimitTours));
